'use client';

import type { ReactNode } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { LegacyDialog } from '../../components/dialog';
import { Button } from '../../components/button';
import { Textarea } from '../../components/textarea';
import { MetaDefault } from '../../components/typography';

type ChangeReasonDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  selectionLabel?: string;
  selection?: ReactNode;
  extraContent?: ReactNode;
  value: string;
  onValueChange: (value: string) => void;
  placeholder: string;
  cancelLabel: string;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  isSubmitDisabled?: boolean;
  disableCloseWhileSubmitting?: boolean;
};

export function ChangeReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  selectionLabel,
  selection,
  extraContent,
  value,
  onValueChange,
  placeholder,
  cancelLabel,
  submitLabel,
  onCancel,
  onSubmit,
  isSubmitting = false,
  isSubmitDisabled = false,
  disableCloseWhileSubmitting = false,
}: ChangeReasonDialogProps) {
  const handleOpenChange = (nextOpen: boolean) => {
    if (disableCloseWhileSubmitting && !nextOpen && isSubmitting) {
      return;
    }

    onOpenChange(nextOpen);
  };

  return (
    <LegacyDialog.Root
      open={open}
      onOpenChange={({ open: nextOpen }) => handleOpenChange(nextOpen)}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content maxW="md">
          <VStack gap="3" p="5" alignItems="stretch">
            <VStack gap="1" alignItems="flex-start">
              <LegacyDialog.Title>{title}</LegacyDialog.Title>
            </VStack>

            {!!selection && (
              <HStack gap="2" alignItems="center">
                {!!selectionLabel && (
                  <MetaDefault color="fg.muted">{selectionLabel}</MetaDefault>
                )}
                {selection}
              </HStack>
            )}

            <Box
              p="3"
              borderRadius="md"
              bg="bg.subtle"
              borderWidth="1px"
              borderColor="border.subtle"
            >
              <LegacyDialog.Description>{description}</LegacyDialog.Description>
            </Box>

            {extraContent}

            <Textarea
              value={value}
              onChange={(event) => onValueChange(event.target.value)}
              placeholder={placeholder}
              minHeight="96px"
              fontSize="sm"
              autoFocus
              disabled={isSubmitting}
            />

            <HStack justifyContent="flex-end" gap="2">
              <Button
                size="xs"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {cancelLabel}
              </Button>
              <Button
                size="xs"
                onClick={onSubmit}
                loading={isSubmitting}
                disabled={isSubmitDisabled || isSubmitting}
              >
                {submitLabel}
              </Button>
            </HStack>
          </VStack>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
